import {defineDimension, createBooleanMap} from '@webflow-platform/abilities';
import {
  siteKinds as allSiteKinds,
  type SiteKind,
} from '@packages/domains/site-access/data-types';

const LEGACY_DEFAULT_SITE_KIND: SiteKind = 'webflow';

export type SiteKindsConfig = Partial<Record<SiteKind, boolean>>;
export type SiteKindsStores = {
  SiteDataStore: {
    kind?: SiteKind | null;
  };
};

function createSiteKindExclusion(
  excludedSiteKinds: readonly SiteKind[]
): SiteKindsConfig {
  const excluded = new Set(excludedSiteKinds);

  return Object.fromEntries(
    allSiteKinds.map((siteKind) => [siteKind, !excluded.has(siteKind)])
  ) as Record<SiteKind, boolean>;
}

export const siteKindsDimension = defineDimension<
  SiteKindsConfig,
  SiteKindsStores
>()({
  key: 'siteKinds',
  isValid(state, config) {
    // Default to 'webflow' for legacy sites without a kind — same as the
    // legacy Designer abilities siteKinds sub-selector.
    const currentSiteKind =
      state.SiteDataStore.kind ?? LEGACY_DEFAULT_SITE_KIND;

    return Boolean(config[currentSiteKind]);
  },
  fragments: {
    inSiteKinds: (...kinds: SiteKind[]) => createBooleanMap(kinds, true),
    notInSiteKinds: (...kinds: SiteKind[]) => createSiteKindExclusion(kinds),
  },
  testMatrix(addAxis) {
    addAxis((addCase) => {
      for (const siteKind of allSiteKinds) {
        addCase({
          label: `siteKind === '${siteKind}'`,
          state: {SiteDataStore: {kind: siteKind}},
        });
      }
    });
  },
});

export const {inSiteKinds, notInSiteKinds} = siteKindsDimension.fragments;
